import { can, fmtDate, ORG_ROLES, type BoardPeople, type OrgRole } from "@boem/core/shared";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ApiError, call } from "../api.ts";
import { useBoards } from "../boards.ts";
import { apiBase } from "../org.ts";
import { Avatar } from "../util.tsx";
import { Icon } from "./Icons.tsx";
import { lifecycleApi } from "./Lifecycle.tsx";
import { ErrorLine, Modal } from "./Modal.tsx";

// People on a personal board (docs/design/orgs.md 5): who's on it and their role, the invite row
// (anyone on OEMUAP who can sign in), what each role can do, the former owners, the board's history,
// and Leave. Owners manage it (several owners may); everyone on the board can read it. An invitation
// takes effect at once: nothing to accept, no Teams message.

export const peopleApi = {
  list: () => call<BoardPeople>("GET", `${apiBase()}/people`),
  invite: (memberId: string, role: OrgRole) => call("POST", `${apiBase()}/people`, { memberId, role }),
  setRole: (memberId: string, role: OrgRole) => call("PATCH", `${apiBase()}/people/${encodeURIComponent(memberId)}`, { role }),
  remove: (memberId: string) => call("DELETE", `${apiBase()}/people/${encodeURIComponent(memberId)}`),
  leave: () => call("POST", `${apiBase()}/leave`, {}),
};

const ROLE_LABEL: Record<OrgRole, string> = { owner: "Owner", editor: "Can edit", viewer: "Can view" };

export function PeopleModal({ onClose, onChanged, extra }: { onClose: () => void; onChanged: () => void; extra?: (p: BoardPeople, reload: () => void) => React.ReactNode }) {
  const boards = useBoards();
  const [data, setData] = useState<BoardPeople | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  const [pick, setPick] = useState<string | null>(null);
  const [role, setRole] = useState<OrgRole>("editor");
  const [findOpen, setFindOpen] = useState(false);
  const load = useCallback(() => peopleApi.list().then(setData, (e) => setError(e instanceof ApiError ? e.message : "Couldn't reach the server.")), []);
  useEffect(() => {
    load();
  }, [load]);

  const me = data?.people.find((p) => p.memberId === boardsViewerId(boards));
  const name = boards?.org.name ?? "this board";
  // owners manage the board (a frozen one: nobody, until a platform admin hands it over)
  const manage = !!me && me.role === "owner" && !data?.frozenAt;
  const owners = (data?.people ?? []).filter((p) => p.role === "owner").length;
  const matches = useMemo(() => {
    const t = q.trim().toLowerCase();
    return (data?.invitable ?? []).filter((m) => !t || m.name.toLowerCase().includes(t)).slice(0, 12);
  }, [data, q]);

  const act = async (fn: () => Promise<unknown>, after?: () => void) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await load();
      onChanged();
      after?.();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Couldn't reach the server. Try again.");
    } finally {
      setBusy(false);
    }
  };
  const picked = data?.invitable.find((m) => m.id === pick) ?? null;

  return (
    <Modal onClose={onClose} wide>
      <div className="org-dlg people">
        <div className="dlg-eyebrow">
          <span className="org-tag pe">
            <Icon name="lock" /> {name}
          </span>
          <span>People</span>
        </div>
        <h3>People on {name}</h3>
        <p className="ppl-lead">Only the people here can open this board. Nothing on it shows on OEMUAP: not the items, not who&rsquo;s here.</p>
        {data?.frozenAt && (
          <p className="frozen-note">
            <b>Read-only since {fmtDate(data.frozenAt)}:</b> its last owner left the company. A platform admin can make someone its owner.
          </p>
        )}
        {!data ? (
          <div className="empty">Loading&hellip;</div>
        ) : (
          <>
            <ul className="ppl" aria-label="On this board">
              {data.people.map((p) => {
                const self = p.memberId === me?.memberId;
                const sub = p.creator && !p.invitedBy ? `made the board ${fmtDate(p.invitedAt)}` : `${p.invitedBy ? `invited by ${p.invitedBy === me?.name ? "you" : p.invitedBy}` : "added"}, ${fmtDate(p.invitedAt)}`;
                return (
                  <li key={p.memberId}>
                    <span className="pav">
                      <Avatar name={p.name} />
                    </span>
                    <span className="nm">
                      {p.name}
                      {self ? " (you)" : ""}
                    </span>
                    {manage && !self ? (
                      <>
                        <select
                          className="search"
                          aria-label={`${p.name}'s role`}
                          value={p.role}
                          disabled={busy}
                          onChange={(e) => act(() => peopleApi.setRole(p.memberId, e.target.value as OrgRole))}
                        >
                          {ORG_ROLES.map((r) => (
                            <option key={r.id} value={r.id}>
                              {ROLE_LABEL[r.id]}
                            </option>
                          ))}
                        </select>
                        <button type="button" className="chip sm rm" disabled={busy} onClick={() => act(() => peopleApi.remove(p.memberId))}>
                          Remove
                        </button>
                      </>
                    ) : (
                      <span className="role-fixed">{ROLE_LABEL[p.role]}</span>
                    )}
                    <span className="sub">{sub}</span>
                  </li>
                );
              })}
            </ul>

            {manage && (
              <>
                <div className="fl">Invite someone from OEMUAP</div>
                <div className="inv-row">
                  <div className="inv-find rel">
                    <input
                      className="search"
                      type="search"
                      placeholder="Find a person…"
                      aria-label="Find a person to invite"
                      value={picked && !findOpen ? picked.name : q}
                      onFocus={() => setFindOpen(true)}
                      onChange={(e) => {
                        setQ(e.target.value);
                        setPick(null);
                        setFindOpen(true);
                      }}
                    />
                    {findOpen && (
                      <div className="inv-pop" role="listbox" aria-label="People on OEMUAP">
                        {matches.length ? (
                          matches.map((m) => (
                            <button
                              key={m.id}
                              type="button"
                              role="option"
                              aria-selected={pick === m.id}
                              className="pk-row"
                              onClick={() => {
                                setPick(m.id);
                                setQ("");
                                setFindOpen(false);
                              }}
                            >
                              <Avatar name={m.name} />
                              <span>{m.name}</span>
                              <span className="rl">{m.role}</span>
                            </button>
                          ))
                        ) : (
                          <div className="pk-none">Nobody else to invite{q ? ` called “${q}”` : ""}.</div>
                        )}
                      </div>
                    )}
                  </div>
                  <select className="search" aria-label="Their role" value={role} onChange={(e) => setRole(e.target.value as OrgRole)}>
                    {ORG_ROLES.map((r) => (
                      <option key={r.id} value={r.id}>
                        {ROLE_LABEL[r.id]}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="btn primary"
                    disabled={!picked || busy}
                    onClick={() =>
                      picked &&
                      act(
                        () => peopleApi.invite(picked.id, role),
                        () => {
                          setPick(null);
                          setQ("");
                        },
                      )
                    }
                  >
                    Invite
                  </button>
                </div>
                <p className="ppl-note">It takes effect at once: the board shows in their switcher under Shared with you. Nothing to accept, and no Teams message.</p>
              </>
            )}

            <div className="roles">
              {ORG_ROLES.map((r) => (
                <div key={r.id}>
                  <b>{ROLE_LABEL[r.id]}</b>
                  {r.can}
                </div>
              ))}
            </div>

            {data.formerOwners.length > 0 && (
              <>
                <div className="fl">Former owners</div>
                <ul className="ppl former" aria-label="Former owners">
                  {data.formerOwners.map((p) => (
                    <li key={p.memberId}>
                      <span className="pav">
                        <Avatar name={p.name} />
                      </span>
                      <span className="nm">{p.name}</span>
                      <span className="role-fixed">Former owner</span>
                      <span className="sub">owner until they left the company</span>
                    </li>
                  ))}
                </ul>
              </>
            )}

            {extra?.(data, load)}

            {data.events.length > 0 && (
              <details className="ppl-hist">
                <summary>This board&rsquo;s history &middot; {data.events.length}</summary>
                <ul>
                  {data.events.map((e, i) => (
                    <li key={i}>
                      <span className="when">{fmtDate(e.at)}</span> {e.actor ? <b>{e.actor}</b> : "The board"} {e.text}
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </>
        )}
        <ErrorLine error={error} />
        <div className="row">
          {manage && (
            <button
              type="button"
              className="btn danger"
              disabled={busy}
              title="Gone from everyone's switcher; kept in the database, and a platform admin can restore it"
              onClick={() =>
                window.confirm(`Delete ${name}? It goes from everyone's switcher and its link stops working. Nothing is erased: a platform admin can restore it.`) &&
                act(
                  () => lifecycleApi.closeBoard(),
                  () => {
                    window.location.href = "/";
                  },
                )
              }
            >
              Delete board
            </button>
          )}
          {me && !data?.frozenAt && !(me.role === "owner" && owners <= 1) && (
            <button
              type="button"
              className="btn"
              disabled={busy}
              title="You won't see this board any more; an owner can invite you again"
              onClick={() =>
                act(
                  () => peopleApi.leave(),
                  () => {
                    window.location.href = "/";
                  },
                )
              }
            >
              Leave the board
            </button>
          )}
          <span className="sp" />
          <button type="button" className="btn primary" onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    </Modal>
  );
}

/** The signed-in person's id, from the board's viewer (People needs to know which row is "you"). */
function boardsViewerId(boards: ReturnType<typeof useBoards>): string | null {
  return boards?.viewerId ?? null;
}

/** May the People screen offer managing? (for the masthead's button title) */
export const managesPeople = (viewer: Parameters<typeof can>[0]) => can(viewer, "invite");
